import React from 'react';
import { Box, FormControlLabel, Radio, RadioGroup, Checkbox, Typography } from '@mui/material';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { SurveyQuestionInterface, SurveyQuestionTypeEnum } from '@families-link/shared';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { useTranslation } from 'react-i18next';
import { CoreFormControl } from '../../../_core/components/_ui/core-form-control';

interface SurveyQuestionRenderProps {
	question: SurveyQuestionInterface;
	translatedQuestion?: any;
	customLabel: string;
}

export const SurveyQuestionRender: React.FC<SurveyQuestionRenderProps> = (props) => {
	const { question, translatedQuestion, customLabel } = props;
	const { _id, text, type, isRequired } = question;
	const options = translatedQuestion
		? question.options?.map((option: any) => {
				const translatedOption = translatedQuestion.options.find(
					(translatedOption: any) => translatedOption.optionId === option._id
				);
				return { ...option, translatedText: translatedOption.translatedText };
		  })
		: question.options;
	const [t] = useTranslation();

	const formContext = useFormContext();

	switch (type) {
		case SurveyQuestionTypeEnum.ShortText:
			return (
				<CoreTextField
					{...formContext?.register(_id ? `field_${_id}` : text)}
					fullWidth
					placeholder={t('survey-templates.forms.placeholders.survey-answer') ?? ''}
					error={!!formContext?.formState.errors[`field_${_id}`]}
					helperText={(formContext?.formState.errors[`field_${_id}`] as FieldError)?.message}
					requiredMark={isRequired}
					label={customLabel}
					customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
				/>
			);
		case SurveyQuestionTypeEnum.LongText:
			return (
				<CoreTextField
					{...formContext?.register(_id ? `field_${_id}` : text)}
					fullWidth
					multiline
					placeholder={t('survey-templates.forms.placeholders.survey-answer') ?? ''}
					error={!!formContext?.formState.errors[`field_${_id}`]}
					helperText={(formContext?.formState.errors[`field_${_id}`] as FieldError)?.message}
					requiredMark={isRequired}
					label={customLabel}
					customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
				/>
			);
		case SurveyQuestionTypeEnum.Options:
			return formContext?.control ? (
				<Box>
					<Controller
						name={_id ? `option_field_${_id}` : text}
						control={formContext?.control ?? undefined}
						defaultValue=""
						render={({ field }) => (
							<CoreFormControl
								label={customLabel}
								customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
								error={!!formContext?.formState.errors[`option_field_${_id}`]}
								helperText={(formContext?.formState.errors[`option_field_${_id}`] as FieldError)?.message}
								requiredMark={isRequired}
								fullWidth
							>
								<RadioGroup {...field} value={field.value || ''} onChange={(e) => field.onChange(e.target.value)}>
									{options?.map((option: any, index: number) => (
										<FormControlLabel
											key={index}
											value={JSON.stringify(option)}
											control={<Radio />}
											label={option?.translatedText || option.text}
										/>
									))}
								</RadioGroup>
							</CoreFormControl>
						)}
					/>
					{formContext?.watch &&
					formContext?.watch(`option_field_${_id}`) &&
					JSON.parse(formContext?.watch(`option_field_${_id}`))?.isOtherOption ? (
						<CoreTextField
							{...formContext?.register(_id ? `additional_option_field_${_id}` : text, {
								required: 'You selected an option that needs more information. Please fill in the field below.',
							})}
							fullWidth
							multiline
							error={!!formContext?.formState.errors[`additional_option_field_${_id}`]}
							helperText={(formContext?.formState.errors[`additional_option_field_${_id}`] as FieldError)?.message}
							requiredMark={isRequired}
							placeholder={'Add relevant information here...'}
						/>
					) : null}
				</Box>
			) : (
				<CoreFormControl
					label={customLabel}
					customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
					requiredMark={isRequired}
					fullWidth
				>
					<RadioGroup>
						{options?.map((option: any, index: number) => (
							<FormControlLabel
								key={index}
								value={option?.translatedText || option.text}
								control={<Radio />}
								label={option?.translatedText || option.text}
							/>
						))}
					</RadioGroup>
				</CoreFormControl>
			);
		case SurveyQuestionTypeEnum.MultiSelect:
			return formContext?.control ? (
				<Box>
					<Controller
						name={_id ? `option_field_${_id}` : text}
						control={formContext.control ?? undefined}
						defaultValue={[]}
						render={({ field }) => (
							<CoreFormControl
								label={customLabel}
								customLabelSx={{ fontSize: '14px', fontWeight: '500', color: 'black' }}
								error={!!formContext?.formState.errors[`option_field_${_id}`]}
								helperText={(formContext?.formState.errors[`option_field_${_id}`] as FieldError)?.message}
								requiredMark={isRequired}
								fullWidth
							>
								{options?.map((option: any, index: number) => (
									<FormControlLabel
										key={index}
										control={
											<Checkbox
												{...field}
												value={JSON.stringify(option)}
												checked={field.value.includes(JSON.stringify(option))}
												onChange={(e) => {
													const newValue = e.target.checked
														? [...field.value, JSON.stringify(option)]
														: field.value.filter((val: string) => JSON.parse(val)?._id !== option._id);
													field.onChange(newValue);
												}}
											/>
										}
										label={option?.translatedText || option.text}
									/>
								))}
							</CoreFormControl>
						)}
					/>
					{formContext?.watch &&
					formContext?.watch(`option_field_${_id}`)?.length &&
					formContext?.watch(`option_field_${_id}`)?.find((el: any) => JSON.parse(el)?.isOtherOption) ? (
						<CoreTextField
							{...formContext?.register(_id ? `additional_option_field_${_id}` : text, {
								required: 'You selected an option that needs more information. Please fill in the field below.',
							})}
							fullWidth
							multiline
							error={!!formContext?.formState.errors[`additional_option_field_${_id}`]}
							helperText={(formContext?.formState.errors[`additional_option_field_${_id}`] as FieldError)?.message}
							requiredMark={isRequired}
							placeholder={'Add relevant information here...'}
						/>
					) : null}
				</Box>
			) : (
				<CoreFormControl
					label={customLabel}
					customLabelSx={{ fontSize: '16px', fontWeight: '500', color: 'black' }}
					requiredMark={isRequired}
					fullWidth
				>
					{options?.map((option: any, index: number) => (
						<FormControlLabel
							key={index}
							control={<Checkbox value={option?.translatedText || option.text} />}
							label={option?.translatedText || option.text}
						/>
					))}
				</CoreFormControl>
			);
		default:
			return null;
	}
};
